import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { notifyWithdrawalValidated, notifyWithdrawalRejected } from '../../../utils/whatsappNotification';
import { siteKey } from '../../../utils/sharedStorage';

interface Withdrawal {
  id: string;
  userPhone: string;
  userName: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  accountDetails: string;
  status: string;
  date: string;
  type?: 'parrainage' | 'taches' | 'points';
  method?: string;
  accountNumber?: string;
}

export default function WithdrawalValidation() {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [filter, setFilter] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const isFetchingRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ✅ États pour suppression
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{ open: boolean; id?: string; name?: string } | null>(null);
  const [deleteAllModal, setDeleteAllModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ✅ Modale suppression en masse des retraits en attente
  const [deletePendingModal, setDeletePendingModal] = useState(false);
  const [isDeletingPending, setIsDeletingPending] = useState(false);

  useEffect(() => {
    loadWithdrawals(false);
    const interval = setInterval(() => {
      if (!isFetchingRef.current) loadWithdrawals(true);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [searchTerm]);

  const loadWithdrawals = async (silent = false) => {
    if (isFetchingRef.current && silent) return;
    isFetchingRef.current = true;
    if (!silent) setIsLoading(true);
    else setIsSyncing(true);

    try {
      // ✅ Charger depuis TOUTES les clés possibles (avec et sans préfixe)
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      const allWithdrawals: Withdrawal[] = [];
      const seenIds = new Set<string>();

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed: Withdrawal[] = JSON.parse(raw);
            parsed.forEach(w => {
              if (!seenIds.has(w.id)) {
                seenIds.add(w.id);
                allWithdrawals.push(w);
              }
            });
          }
        } catch (e) {
          console.error(`Erreur parsing ${key}:`, e);
        }
      }

      allWithdrawals.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setWithdrawals(allWithdrawals);
      setLastSync(new Date());
    } catch (e: any) {
      console.error('Erreur chargement retraits:', e);
      showError('⚠️ Erreur lors du chargement des retraits');
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
      setIsSyncing(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setErrorMsg('');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const showError = (msg: string) => {
    setErrorMsg(msg);
    setSuccessMsg('');
    setTimeout(() => setErrorMsg(''), 5000);
  };

  const handleValidateWithdrawal = async (withdrawalId: string) => {
    const withdrawal = withdrawals.find((w) => w.id === withdrawalId);
    if (!withdrawal) return;
    setIsLoading(true);
    try {
      // ✅ Calculer le montant net (montant - 400F de frais)
      const netAmount = withdrawal.amount - 400;

      // ✅ Mettre à jour le statut dans TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.map(w => w.id === withdrawalId ? { ...w, status: 'validated' } : w);
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur mise à jour ${key}:`, e);
        }
      }

      // ✅ Envoyer un signal BroadcastChannel pour mise à jour en temps réel
      try {
        const withdrawalChannel = new BroadcastChannel('withdrawal_update');
        withdrawalChannel.postMessage({ 
          withdrawalId, 
          status: 'validated',
          timestamp: Date.now()
        });
        withdrawalChannel.close();
      } catch (e) { 
        console.warn('BroadcastChannel non supporté:', e); 
      }

      setWithdrawals(prev => prev.map(w => w.id === withdrawalId ? { ...w, status: 'validated' } : w));

      try {
        await notifyWithdrawalValidated(withdrawal.userPhone, withdrawal.userName || 'Utilisateur', netAmount, withdrawal.currency || 'FCFA', 'fr');
      } catch { /* ignore */ }

      showSuccess(`✅ Retrait de ${withdrawal.userName} validé ! Montant à payer: ${netAmount.toLocaleString()} ${withdrawal.currency || 'FCFA'}`);
    } catch (e: any) {
      console.error('Erreur validation retrait:', e);
      showError(`⚠️ Erreur lors de la validation : ${e?.message || 'Veuillez réessayer.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectWithdrawal = async (withdrawalId: string) => {
    const withdrawal = withdrawals.find((w) => w.id === withdrawalId);
    setIsLoading(true);
    try {
      // ✅ Mettre à jour le statut dans TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.map(w => w.id === withdrawalId ? { ...w, status: 'rejected' } : w);
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur mise à jour ${key}:`, e);
        }
      }

      // ✅ Envoyer un signal BroadcastChannel pour mise à jour en temps réel
      try {
        const withdrawalChannel = new BroadcastChannel('withdrawal_update');
        withdrawalChannel.postMessage({ 
          withdrawalId, 
          status: 'rejected',
          timestamp: Date.now()
        });
        withdrawalChannel.close();
      } catch (e) { 
        console.warn('BroadcastChannel non supporté:', e); 
      }

      setWithdrawals(prev => prev.map(w => w.id === withdrawalId ? { ...w, status: 'rejected' } : w));

      if (withdrawal) {
        try {
          await notifyWithdrawalRejected(withdrawal.userPhone, withdrawal.userName || 'Utilisateur', withdrawal.amount, withdrawal.currency || 'FCFA', 'fr');
        } catch { /* ignore */ }
        showSuccess(`❌ Retrait de ${withdrawal.userName} rejeté.`);
      }
    } catch (e: any) {
      console.error('Erreur rejet retrait:', e);
      showError(`⚠️ Erreur lors du rejet : ${e?.message || 'Veuillez réessayer.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Suppression individuelle
  const confirmDeleteWithdrawal = (id: string, name: string) => {
    setDeleteConfirmModal({ open: true, id, name });
  };

  const handleDeleteWithdrawal = async () => {
    if (!deleteConfirmModal?.id) return;
    setIsDeleting(true);
    try {
      // Supprimer de TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.filter(w => w.id !== deleteConfirmModal.id);
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur suppression ${key}:`, e);
        }
      }

      setWithdrawals(prev => prev.filter(w => w.id !== deleteConfirmModal.id));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(deleteConfirmModal.id!);
        return newSet;
      });

      showSuccess(`✅ Retrait de ${deleteConfirmModal.name} supprimé avec succès !`);
      setDeleteConfirmModal(null);
    } catch (error) {
      console.error('Erreur suppression retrait:', error);
      showError('⚠️ Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ Suppression en masse
  const confirmDeleteAll = () => {
    setDeleteAllModal(true);
  };

  const handleDeleteAll = async () => {
    setIsDeleting(true);
    try {
      const toDelete = filteredWithdrawals;
      const idsToDelete = toDelete.map(w => w.id);

      // Supprimer de TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.filter(w => !idsToDelete.includes(w.id));
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur suppression ${key}:`, e);
        }
      }

      setWithdrawals(prev => prev.filter(w => !idsToDelete.includes(w.id)));
      setSelectedIds(new Set());

      showSuccess(`✅ ${toDelete.length} retrait(s) supprimé(s) avec succès !`);
      setDeleteAllModal(false);
    } catch (error) {
      console.error('Erreur suppression en masse:', error);
      showError('⚠️ Erreur lors de la suppression en masse. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ Suppression de la sélection
  const handleDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    setIsDeleting(true);
    try {
      const idsToDelete = Array.from(selectedIds);

      // Supprimer de TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.filter(w => !idsToDelete.includes(w.id));
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur suppression ${key}:`, e);
        }
      }

      setWithdrawals(prev => prev.filter(w => !idsToDelete.includes(w.id)));
      setSelectedIds(new Set());

      showSuccess(`✅ ${idsToDelete.length} retrait(s) supprimé(s) avec succès !`);
    } catch (error) {
      console.error('Erreur suppression sélection:', error);
      showError('⚠️ Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ Modale suppression retraits en attente
  const handleDeleteAllPending = async () => {
    setIsDeletingPending(true);
    try {
      const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending');

      // Supprimer de TOUTES les clés localStorage
      const allKeys = [
        siteKey('withdrawalRequests'),
        'withdrawalRequests'
      ];

      for (const key of allKeys) {
        try {
          const raw = localStorage.getItem(key) || '[]';
          let saved: Withdrawal[] = JSON.parse(raw);
          saved = saved.filter(w => w.status !== 'pending');
          localStorage.setItem(key, JSON.stringify(saved));
        } catch (e) {
          console.error(`Erreur suppression ${key}:`, e);
        }
      }

      setWithdrawals(prev => prev.filter(w => w.status !== 'pending'));
      setSelectedIds(new Set());

      showSuccess(`✅ ${pendingWithdrawals.length} retrait(s) en attente supprimé(s) avec succès !`);
      setDeletePendingModal(false);
    } catch (error) {
      console.error('Erreur suppression retraits en attente:', error);
      showError('⚠️ Erreur lors de la suppression. Veuillez réessayer.');
    } finally {
      setIsDeletingPending(false);
    }
  };

  // ✅ Sélection / Désélection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredWithdrawals.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredWithdrawals.map(w => w.id)));
    }
  };

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const filteredWithdrawals = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return withdrawals.filter((w) => {
      const matchStatus = filter === 'all' || w.status === filter;
      if (!matchStatus) return false;
      if (!q) return true;
      const haystack = [w.userName || '', w.userPhone || '', w.accountDetails || '', w.accountNumber || ''].join('|').toLowerCase();
      return haystack.includes(q);
    });
  }, [withdrawals, filter, debouncedSearch]);

  const counts = useMemo(() => ({
    pending: withdrawals.filter(w => w.status === 'pending').length,
    validated: withdrawals.filter(w => w.status === 'validated').length,
    rejected: withdrawals.filter(w => w.status === 'rejected').length,
    all: withdrawals.length,
  }), [withdrawals]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return dateString; }
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'parrainage': return 'Parrainage';
      case 'taches': return 'Tâches';
      case 'points': return 'Points';
      default: return type || '';
    }
  };

  const getTypeColor = (type?: string) => {
    switch (type) {
      case 'parrainage': return 'bg-orange-100 text-orange-700';
      case 'taches': return 'bg-emerald-100 text-emerald-700';
      case 'points': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div>
      {isLoading && (
        <div className="fixed top-4 right-4 z-50 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
          <i className="ri-loader-4-line animate-spin"></i>
          <span className="text-sm font-semibold">Chargement...</span>
        </div>
      )}
      {isSyncing && !isLoading && (
        <div className="fixed top-4 right-4 z-50 bg-gray-700 text-white px-3 py-1.5 rounded-lg shadow flex items-center gap-2 opacity-70">
          <i className="ri-refresh-line animate-spin text-sm"></i>
          <span className="text-xs">Sync...</span>
        </div>
      )}

      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Validation des retraits</h2>
          </div>
          <div className="flex items-center gap-3">
            {lastSync && (
              <span className="text-xs text-gray-400 hidden sm:block">
                Sync: {lastSync.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              onClick={() => loadWithdrawals(false)}
              disabled={isLoading}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
            >
              <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
              Actualiser
            </button>
            {counts.pending > 0 && (
              <>
                <button
                  onClick={() => setDeletePendingModal(true)}
                  disabled={isLoading}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line"></i>
                  Supprimer tout en attente ({counts.pending})
                </button>
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-bold animate-pulse">
                  <i className="ri-alarm-warning-line"></i>
                  {counts.pending} en attente
                </span>
              </>
            )}
          </div>
        </div>
        <p className="text-sm text-gray-500">
          Total : <strong>{withdrawals.length}</strong> retrait{withdrawals.length > 1 ? 's' : ''} chargés depuis le stockage local.
        </p>
      </div>

      {successMsg && (
        <div className="mb-4 px-4 py-3 bg-green-100 text-green-700 rounded-lg flex items-center gap-2 font-semibold text-sm">
          <i className="ri-checkbox-circle-fill text-lg"></i> {successMsg}
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 px-4 py-3 bg-red-100 text-red-700 rounded-lg flex items-center gap-2 font-semibold text-sm">
          <i className="ri-error-warning-fill text-lg"></i> {errorMsg}
        </div>
      )}

      {/* Barre de recherche */}
      <div className="relative mb-4">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Rechercher par nom, téléphone ou numéro de compte..."
          className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-400 focus:border-transparent outline-none bg-white shadow-sm"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-circle-fill text-lg"></i>
          </button>
        )}
      </div>
      {debouncedSearch && (
        <p className="text-xs text-gray-500 mb-3 ml-1 flex items-center gap-1">
          <i className="ri-filter-3-line"></i>
          <strong>{filteredWithdrawals.length}</strong> résultat{filteredWithdrawals.length > 1 ? 's' : ''} pour "<em>{debouncedSearch}</em>"
        </p>
      )}

      {/* Filtres */}
      <div className="flex flex-wrap gap-2 mb-6">
        {([
          { key: 'pending', label: 'En attente', count: counts.pending },
          { key: 'validated', label: 'Validés', count: counts.validated },
          { key: 'rejected', label: 'Rejetés', count: counts.rejected },
          { key: 'all', label: 'Tous', count: counts.all },
        ] as const).map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap ${
              filter === f.key ? 'bg-red-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${filter === f.key ? 'bg-white/20 text-white' : 'bg-gray-300 text-gray-600'}`}>
              {f.count}
            </span>
          </button>
        ))}
      </div>

      {/* ✅ Barre de sélection */}
      {filteredWithdrawals.length > 0 && (
        <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedIds.size === filteredWithdrawals.length && filteredWithdrawals.length > 0}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-red-600 rounded cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">
                {selectedIds.size === filteredWithdrawals.length && filteredWithdrawals.length > 0 ? 'Tout désélectionner' : 'Tout sélectionner'}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <span className="text-sm text-gray-600">
                <strong>{selectedIds.size}</strong> sélectionné{selectedIds.size > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
              >
                <i className="ri-delete-bin-line"></i>
                Supprimer la sélection ({selectedIds.size})
              </button>
            )}
            <button
              onClick={confirmDeleteAll}
              disabled={isDeleting}
              className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition cursor-pointer whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
            >
              <i className="ri-delete-bin-line"></i>
              Supprimer tout ({filteredWithdrawals.length})
            </button>
          </div>
        </div>
      )}

      {filteredWithdrawals.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
            <i className={`${debouncedSearch ? 'ri-search-line' : 'ri-inbox-line'} text-4xl text-gray-300`}></i>
          </div>
          <p className="text-gray-500 font-medium">
            {debouncedSearch ? `Aucun résultat pour "${debouncedSearch}"` : 'Aucun retrait à afficher'}
          </p>
          <p className="text-gray-400 text-sm mt-1">
            {debouncedSearch
              ? 'Essayez avec un autre nom ou numéro.'
              : filter === 'pending'
              ? 'Aucun retrait en attente pour le moment.'
              : 'Aucune entrée dans cette catégorie.'}
          </p>
          {debouncedSearch && (
            <button onClick={() => setSearchTerm('')} className="mt-3 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition">
              Effacer la recherche
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredWithdrawals.map((withdrawal) => {
            // ✅ Calculer le montant net (montant - 400F de frais)
            const netAmount = withdrawal.amount - 400;
            
            return (
              <div
                key={withdrawal.id}
                className={`bg-white border rounded-xl p-5 hover:shadow-md transition border-l-4 ${
                  withdrawal.status === 'pending' ? 'border-l-yellow-400 border-gray-200' :
                  withdrawal.status === 'validated' ? 'border-l-green-500 border-gray-200' :
                  'border-l-red-400 border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  {/* ✅ Case à cocher */}
                  <div className="flex items-start gap-3 flex-1">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(withdrawal.id)}
                      onChange={() => toggleSelect(withdrawal.id)}
                      className="mt-1 w-4 h-4 text-red-600 rounded cursor-pointer flex-shrink-0"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full flex-shrink-0">
                          <i className="ri-money-dollar-circle-line text-xl text-gray-600"></i>
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">{withdrawal.userName || 'Utilisateur'}</h3>
                          <p className="text-xs text-gray-500">{withdrawal.userPhone || 'N/A'}</p>
                        </div>
                        <div className="ml-auto flex items-center gap-2 flex-wrap">
                          {withdrawal.type && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(withdrawal.type)}`}>
                              {getTypeLabel(withdrawal.type)}
                            </span>
                          )}
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                            withdrawal.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            withdrawal.status === 'validated' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {withdrawal.status === 'pending' ? '⏳ En attente' : withdrawal.status === 'validated' ? 'Validé ✅' : 'Rejeté ❌'}
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-400 font-medium">Montant demandé</p>
                          <p className="font-bold text-gray-700 text-base">{withdrawal.amount?.toLocaleString()} {withdrawal.currency || 'FCFA'}</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-200">
                          <p className="text-xs text-emerald-600 font-medium">💰 Montant à payer</p>
                          <p className="font-bold text-emerald-700 text-base">{netAmount.toLocaleString()} {withdrawal.currency || 'FCFA'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">Frais: -400 {withdrawal.currency || 'FCFA'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-400 font-medium">Méthode</p>
                          <p className="font-semibold text-gray-800 text-sm">{withdrawal.paymentMethod || withdrawal.method || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-3 py-2">
                          <p className="text-xs text-gray-400 font-medium">Compte / Numéro</p>
                          <p className="font-semibold text-gray-800 text-sm break-all">{withdrawal.accountDetails || withdrawal.accountNumber || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg px-3 py-2 col-span-2">
                          <p className="text-xs text-gray-400 font-medium">Date</p>
                          <p className="font-semibold text-gray-800 text-sm">{formatDate(withdrawal.date)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {withdrawal.status === 'pending' && (
                  <div className="flex gap-3 mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleRejectWithdrawal(withdrawal.id)}
                      disabled={isLoading}
                      className="flex-1 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 border border-red-200 disabled:opacity-50"
                    >
                      <i className="ri-close-circle-line text-lg"></i>
                      Rejeter
                    </button>
                    <button
                      onClick={() => handleValidateWithdrawal(withdrawal.id)}
                      disabled={isLoading}
                      className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50"
                    >
                      <i className="ri-checkbox-circle-line text-lg"></i>
                      Valider le retrait
                    </button>
                    <button
                      onClick={() => confirmDeleteWithdrawal(withdrawal.id, withdrawal.userName)}
                      disabled={isLoading}
                      className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-red-600 font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
                      title="Supprimer"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                )}

                {withdrawal.status !== 'pending' && (
                  <div className="flex justify-end mt-4 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => confirmDeleteWithdrawal(withdrawal.id, withdrawal.userName)}
                      disabled={isLoading}
                      className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-semibold rounded-lg text-sm transition cursor-pointer whitespace-nowrap flex items-center gap-2 border border-red-200 disabled:opacity-50"
                    >
                      <i className="ri-delete-bin-line"></i>
                      Supprimer
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ✅ Modale confirmation suppression individuelle */}
      {deleteConfirmModal?.open && deleteConfirmModal.id && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
                <p className="text-sm text-gray-500">Cette action est irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800">
                Êtes-vous sûr de vouloir supprimer le retrait de <strong>{deleteConfirmModal.name}</strong> ?
              </p>
              <p className="text-xs text-red-600 mt-2">
                Cette demande sera définitivement supprimée.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmModal(null)}
                disabled={isDeleting}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteWithdrawal}
                disabled={isDeleting}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50"
              >
                {isDeleting ? (
                  <><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>
                ) : (
                  <><i className="ri-delete-bin-line text-lg"></i>Supprimer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modale suppression en masse */}
      {deleteAllModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Suppression en masse</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800 font-semibold mb-2">
                ⚠️ Vous allez supprimer <strong>{filteredWithdrawals.length}</strong> retrait{filteredWithdrawals.length > 1 ? 's' : ''} !
              </p>
              <p className="text-xs text-red-600">
                Toutes ces demandes seront définitivement supprimées de la base de données.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteAllModal(false)}
                disabled={isDeleting}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAll}
                disabled={isDeleting}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50"
              >
                {isDeleting ? (
                  <><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>
                ) : (
                  <><i className="ri-delete-bin-line text-lg"></i>Tout supprimer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Modale suppression retraits en attente */}
      {deletePendingModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-alarm-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Supprimer tous les retraits en attente</h3>
                <p className="text-sm text-gray-500">Action irréversible</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-800 font-semibold mb-2">
                ⚠️ Vous allez supprimer <strong>{withdrawals.filter(w => w.status === 'pending').length}</strong> retrait{withdrawals.filter(w => w.status === 'pending').length > 1 ? 's' : ''} en attente !
              </p>
              <p className="text-xs text-red-600">
                Toutes les demandes de retrait en attente seront définitivement supprimées.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletePendingModal(false)}
                disabled={isDeletingPending}
                className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl text-sm transition cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAllPending}
                disabled={isDeletingPending}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl text-sm transition cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 shadow disabled:opacity-50"
              >
                {isDeletingPending ? (
                  <><i className="ri-loader-4-line animate-spin text-lg"></i>Suppression...</>
                ) : (
                  <><i className="ri-delete-bin-line text-lg"></i>Tout supprimer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}